import apiMiddlewareActionTypes from "App/apiMiddleware/redux/actionTypes";
import listenerMiddleware from "App/listenerMiddleware";
import { error } from "App/popupMessage";
import documentEditorActionTypes from "Areas/Documents/documentEditorPanel/redux/actionTypes";
import documentsActionTypes from "Areas/Documents/redux/actionTypes";
import connectionsActionTypes from "Areas/Settings/connections/redux/actionTypes";
import ontologicalModelActionTypes from "Areas/Settings/ontologicalModel/redux/actionTypes";
import { getDraftData } from "Areas/Settings/ontologicalModel/redux/selectors";
import { actionTypes as signalRActionTypes } from "Areas/SharedFeatures/signalRMiddleware";
import { DOCUMENT_TYPE } from "Common/dictionaries/documentType";
import createReducer from "Common/reducerUtils/createReducer";
import set from "Common/reducerUtils/set";
import difference from "lodash/difference";
import findIndex from "lodash/findIndex";
import forEach from "lodash/forEach";
import head from "lodash/head";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import last from "lodash/last";
import map from "lodash/map";
import omit from "lodash/omit";
import reject from "lodash/reject";
import size from "lodash/size";
import uniq from "lodash/uniq";
import { CONTENT_MODE_TAB, NAMESPACE, TAB } from "../constants";
import actionCreators from "./actionCreators";
import actionTypes from "./actionTypes";
import locale from "./reducer.locale";
import { getObjects } from "./selectors";
export const initialState = {
    [NAMESPACE]: {
        tab: TAB.NAVIGATION,
        contentModeTab: CONTENT_MODE_TAB.FOUND,
        documents: {},
        selectedDocuments: [],
        selectedMethods: {},
        activeDocument: undefined,
        documentType: {},
        objects: undefined,
        objectsOutdated: false,
        objectsUsedByUI: false,
        methods: undefined,
        isExpanded: true,
        navigateToId: undefined,
        scripts: undefined,
        scriptEditorPanel: {
            show: false,
            targetSelector: undefined,
            targetOffset: undefined,
            data: undefined,
            draft: {}
        },
        tasks: {
            data: {},
            mappingToTaskIds: {},
            outdated: []
        },
        sourceTaskIdsToHighlight: {}
    }
};
export default createReducer(initialState, {
    [actionTypes.SET_IS_EXPANDED]: (state, { isExpanded }) => set(state, `[${NAMESPACE}].isExpanded`, isExpanded),
    [actionTypes.SET_NAVIGATE_TO]: (state, { navigateToId }) => set(state, `[${NAMESPACE}].navigateToId`, navigateToId),
    [actionTypes.CLEAR_METHODS_SELECTED]: (state) => set(state, `[${NAMESPACE}].selectedMethods`, {}),
    [actionTypes.TOGGLE_METHOD_SELECTED]: (state, { method, nodesSource, selected }) => {
        if (isEmpty(nodesSource) || (!selected && !state[NAMESPACE].selectedMethods[method.Id]))
            return state;
        if (!selected) {
            const nodeIdsToUnselect = map(nodesSource, "NodeId");
            const resultSelectedMethodNodeSources = reject(state[NAMESPACE].selectedMethods[method.Id], e => includes(nodeIdsToUnselect, e.NodeId));
            return !isEmpty(resultSelectedMethodNodeSources)
                ? set(state, `[${NAMESPACE}].selectedMethods[${method.Id}]`, resultSelectedMethodNodeSources)
                : set(state, `[${NAMESPACE}].selectedMethods`, omit(state[NAMESPACE].selectedMethods, method.Id));
        }
        const resultSelectedMethodNodeSources = [
            ...(state[NAMESPACE].selectedMethods[method.Id] || [])
        ];
        forEach(nodesSource, e => {
            const index = findIndex(resultSelectedMethodNodeSources, ["NodeId", e.NodeId]);
            if (index !== -1)
                resultSelectedMethodNodeSources[index] = e;
            else
                resultSelectedMethodNodeSources.push(e);
        });
        return set(state, `[${NAMESPACE}].selectedMethods[${method.Id}]`, resultSelectedMethodNodeSources);
    },
    [actionTypes.SET_TAB]: (state, { tab }) => set(state, `[${NAMESPACE}].tab`, tab),
    [actionTypes.SET_CONTENT_MODE_TAB]: (state, { contentModeTab }) => set(state, `[${NAMESPACE}].contentModeTab`, contentModeTab),
    [actionTypes.GET_OBJECTS_COMPLETED]: (state, { payload }) => {
        let result = set(state, `[${NAMESPACE}].objects`, keyBy(payload, "Id"));
        result = set(result, `[${NAMESPACE}].objectsOutdated`, false);
        return set(result, `[${NAMESPACE}].objectsUsedByUI`, true);
    },
    [actionTypes.SET_OBJECTS_OUTDATED]: (state) => set(state, `[${NAMESPACE}].objectsOutdated`, true),
    [actionTypes.SET_OBJECTS_USED_BY_UI]: (state) => set(state, `[${NAMESPACE}].objectsUsedByUI`, true),
    [actionTypes.GET_METHODS_COMPLETED]: (state, { payload }) => set(state, `[${NAMESPACE}].methods`, payload),
    [actionTypes.GET_DOCUMENTS_COMPLETED]: (state, { payload, meta }) => set(state, `[${NAMESPACE}].documents[${meta.projectId}]`, payload),
    [actionTypes.SET_DOCUMENT_SELECTED]: (state, { documentId, selected }) => {
        var _a;
        const addToSelected = selected && !includes(state[NAMESPACE].selectedDocuments, documentId);
        const removeFromSelected = !selected && includes(state[NAMESPACE].selectedDocuments, documentId);
        const resultSelectedDocuments = addToSelected
            ? [...state[NAMESPACE].selectedDocuments, documentId]
            : removeFromSelected
                ? reject(state[NAMESPACE].selectedDocuments, id => id === documentId)
                : state[NAMESPACE].selectedDocuments;
        const prevActiveDocument = state[NAMESPACE].activeDocument;
        const resultActiveDocument = selected
            ? documentId
            : prevActiveDocument && includes(resultSelectedDocuments, prevActiveDocument)
                ? prevActiveDocument
                : last(resultSelectedDocuments);
        let resultState = state;
        if (prevActiveDocument !== resultActiveDocument) {
            resultState = set(state, `[${NAMESPACE}].activeDocument`, resultActiveDocument, {
                checkExistence: false,
                checkEqual: false
            });
            resultState = set(resultState, `[${NAMESPACE}].selectedMethods`, {});
        }
        if (!selected && resultState[NAMESPACE].documentType[documentId]) {
            resultState = set(resultState, `[${NAMESPACE}].documentType`, omit(resultState[NAMESPACE].documentType, documentId), {
                checkExistence: false,
                checkEqual: false
            });
        }
        if (!selected && ((_a = resultState[NAMESPACE].sourceTaskIdsToHighlight) === null || _a === void 0 ? void 0 : _a[documentId])) {
            resultState = set(resultState, `[${NAMESPACE}].sourceTaskIdsToHighlight`, omit(resultState[NAMESPACE].sourceTaskIdsToHighlight, documentId), {
                checkExistence: false,
                checkEqual: false
            });
        }
        return set(resultState, `[${NAMESPACE}].selectedDocuments`, resultSelectedDocuments, {
            checkExistence: false,
            checkEqual: false
        });
    },
    [actionTypes.SET_DOCUMENT_TYPE]: (state, { documentId, documentType }) => {
        const resultState = set(state, `[${NAMESPACE}].documentType[${documentId}]`, documentType);
        return documentType !== DOCUMENT_TYPE.TABLE
            ? set(resultState, `[${NAMESPACE}].tab`, TAB.NAVIGATION)
            : resultState;
    },
    [actionTypes.RUN_METHODS_COMPLETED]: (state, { meta }) => state[NAMESPACE].activeDocument === meta.documentId
        ? set(state, `[${NAMESPACE}].selectedMethods`, {})
        : state,
    [actionTypes.SET_SHOW_SCRIPT_EDITOR_PANEL]: (state, { show, data, targetSelector, targetOffset }) => {
        let resultState = set(state, `[${NAMESPACE}].scriptEditorPanel.show`, show);
        if (show) {
            resultState = set(resultState, `[${NAMESPACE}].scriptEditorPanel.data`, data);
            resultState = set(resultState, `[${NAMESPACE}].scriptEditorPanel.targetSelector`, targetSelector);
            resultState = set(resultState, `[${NAMESPACE}].scriptEditorPanel.draft`, {});
            resultState = set(resultState, `[${NAMESPACE}].scriptEditorPanel.targetOffset`, targetOffset);
        }
        return resultState;
    },
    [actionTypes.SAVE_SCRIPT_COMPLETED]: (state, { payload }) => {
        const resultState = set(state, `[${NAMESPACE}].scriptEditorPanel.show`, false);
        return state[NAMESPACE].scripts
            ? set(resultState, `[${NAMESPACE}].scripts[${payload.Id}]`, payload)
            : resultState;
    },
    [actionTypes.SET_SCRIPT_DRAFT]: (state, { draft }) => {
        const isDraftPartial = size(draft) === 1;
        return set(state, `[${NAMESPACE}].scriptEditorPanel.draft`, isDraftPartial && draft ? { ...state[NAMESPACE].scriptEditorPanel.draft, ...draft } : draft, {
            checkExistence: false,
            checkEqual: false
        });
    },
    [actionTypes.GET_SCRIPTS_COMPLETED]: (state, { payload }) => set(state, `[${NAMESPACE}].scripts`, keyBy(payload, "Id")),
    [actionTypes.DELETE_SCRIPT_COMPLETED]: (state, { meta }) => state[NAMESPACE].scripts
        ? set(state, `[${NAMESPACE}].scripts`, omit(state[NAMESPACE].scripts, meta.scriptId))
        : state,
    [actionTypes.GET_TASKS_COMPLETED]: (state, { payload, meta }) => {
        const ids = map(payload, "Id");
        let result = set(state, `[${NAMESPACE}].tasks.mappingToTaskIds[${meta.documentId}]`, ids);
        result = set(result, `[${NAMESPACE}].tasks.outdated`, difference(state[NAMESPACE].tasks.outdated, ids));
        const newData = keyBy(map(payload, e => omit(e, "User.Avatar")), "Id");
        return set(result, `[${NAMESPACE}].tasks.data`, { ...state[NAMESPACE].tasks.data, ...newData });
    },
    [actionTypes.SET_REQUEST_TASK_OUTDATED]: (state, { requestTaskId, documentId }) => {
        let result = set(state, `[${NAMESPACE}].tasks.outdated`, uniq([...(state[NAMESPACE].tasks.outdated || []), requestTaskId]));
        result = set(result, `[${NAMESPACE}].tasks.mappingToTaskIds[${documentId}]`, uniq([...(state[NAMESPACE].tasks.mappingToTaskIds[documentId] || []), requestTaskId]));
        return result;
    },
    [actionTypes.GET_REQUEST_TASK_COMPLETED]: (state, { payload, meta }) => {
        const result = set(state, `[${NAMESPACE}].tasks.data[${meta.requestTaskId}]`, payload);
        return set(result, `[${NAMESPACE}].tasks.outdated`, difference(state[NAMESPACE].tasks.outdated, [meta.requestTaskId]));
    },
    [actionTypes.RESTART_REQUEST_TASK_COMPLETED]: (state, { payload }) => set(state, `[${NAMESPACE}].tasks.data[${payload.Id}]`, payload),
    [actionTypes.STOP_REQUEST_TASK_COMPLETED]: (state, { payload }) => set(state, `[${NAMESPACE}].tasks.data[${payload.Id}]`, payload),
    [actionTypes.SET_SOURCE_TASK_IDS_TO_HIGHLIGHT]: (state, { sourceTaskIds, documentId }) => set(state, `[${NAMESPACE}].sourceTaskIdsToHighlight[${documentId}]`, sourceTaskIds),
    [actionTypes.CLEAR_SOURCE_TASK_IDS_TO_HIGHLIGHT]: (state, { documentId }) => set(state, `[${NAMESPACE}].sourceTaskIdsToHighlight[${documentId}]`, omit(state[NAMESPACE].sourceTaskIdsToHighlight, documentId)),
    [documentsActionTypes.DELETE_DOCUMENT_COMPLETED]: (state, { meta }) => {
        var _a;
        let resultState = set(state, `[${NAMESPACE}].documents[${meta.projectId}]`, reject(state[NAMESPACE].documents[meta.projectId], ["Id", meta.documentId]), {
            checkExistence: true,
            checkEqual: false
        });
        if (!includes(state[NAMESPACE].selectedDocuments, meta.documentId))
            return resultState;
        const resultSelectedDocuments = reject(state[NAMESPACE].selectedDocuments, id => id === meta.documentId);
        resultState = set(resultState, `[${NAMESPACE}].selectedDocuments`, resultSelectedDocuments, {
            checkExistence: false,
            checkEqual: false
        });
        if (resultState[NAMESPACE].documentType[meta.documentId]) {
            resultState = set(resultState, `[${NAMESPACE}].documentType`, omit(resultState[NAMESPACE].documentType, meta.documentId), {
                checkExistence: false,
                checkEqual: false
            });
        }
        if (state[NAMESPACE].activeDocument === meta.documentId) {
            resultState = set(resultState, `[${NAMESPACE}].activeDocument`, head(resultSelectedDocuments), {
                checkExistence: false,
                checkEqual: false
            });
            resultState = set(resultState, `[${NAMESPACE}].selectedMethods`, {});
        }
        if ((_a = resultState[NAMESPACE].sourceTaskIdsToHighlight) === null || _a === void 0 ? void 0 : _a[meta.documentId]) {
            resultState = set(resultState, `[${NAMESPACE}].sourceTaskIdsToHighlight`, omit(resultState[NAMESPACE].sourceTaskIdsToHighlight, meta.documentId), {
                checkExistence: false,
                checkEqual: false
            });
        }
        resultState = set(resultState, `[${NAMESPACE}].tasks.mappingToTaskIds`, omit(state[NAMESPACE].tasks.mappingToTaskIds, meta.documentId));
        return resultState;
    },
    [documentEditorActionTypes.SAVE_DOCUMENT_COMPLETED]: (state, { payload, meta }) => {
        const documents = state[NAMESPACE].documents[payload.ProjectId];
        if (!documents)
            return state;
        const documentIndex = findIndex(documents, ["Id", payload.Id]);
        const isNew = documentIndex === -1;
        const indexToInsertOrUpdate = !isNew ? documentIndex : documents.length;
        let resultState = set(state, `[${NAMESPACE}].documents[${payload.ProjectId}][${indexToInsertOrUpdate}]`, payload, {
            checkExistence: false,
            checkEqual: true
        });
        if (meta.activeNewDocumentAfterCreation && meta.isNew) {
            if (!includes(state[NAMESPACE].selectedDocuments, payload.Id)) {
                resultState = set(resultState, `[${NAMESPACE}].selectedDocuments`, [...state[NAMESPACE].selectedDocuments, payload.Id], {
                    checkExistence: false,
                    checkEqual: false
                });
            }
            if (state[NAMESPACE].activeDocument !== payload.Id) {
                resultState = set(resultState, `[${NAMESPACE}].activeDocument`, payload.Id, {
                    checkExistence: false,
                    checkEqual: false
                });
                resultState = set(resultState, `[${NAMESPACE}].selectedMethods`, {});
            }
        }
        return resultState;
    },
    [documentEditorActionTypes.DUPLICATE_DOCUMENT_COMPLETED]: (state, { payload }) => {
        const documents = state[NAMESPACE].documents[payload.ProjectId];
        if (!documents)
            return state;
        const indexToInsert = documents.length;
        return set(state, `[${NAMESPACE}].documents[${payload.ProjectId}][${indexToInsert}]`, payload, {
            checkExistence: false,
            checkEqual: true
        });
    }
});
const updateTasksCallback = ({ message }, dispatch) => {
    (message === null || message === void 0 ? void 0 : message.requestTaskId) &&
        (message === null || message === void 0 ? void 0 : message.documentId) &&
        dispatch(actionCreators.setRequestTaskOutdated(message.requestTaskId, message.documentId));
};
const loadDocumentObjects = (_, dispatch) => {
    dispatch(actionCreators.getObjects(false, true));
};
listenerMiddleware.addListener({
    [signalRActionTypes.SOURCE_TASK_RUNNING.redux]: updateTasksCallback,
    [signalRActionTypes.SOURCE_TASK_COMPLETED.redux]: updateTasksCallback,
    [signalRActionTypes.REQUEST_TASK_RUNNING.redux]: updateTasksCallback,
    [signalRActionTypes.REQUEST_TASK_COMPLETED.redux]: updateTasksCallback,
    [actionTypes.RUN_METHODS_COMPLETED]: ({ payload, meta }, dispatch) => {
        dispatch(actionCreators.setRequestTaskOutdated(payload, meta.documentId));
    },
    [signalRActionTypes.ONTOLOGICAL_MODEL_UPDATED.redux]: (_, dispatch, getState) => {
        const state = getState();
        if (getObjects(state) || getDraftData(state))
            dispatch(error(locale.ONTOLOGICAL_MODEL_WAS_CHANGED, false));
    },
    [apiMiddlewareActionTypes.REQUEST_STARTED]: ({ scope }, dispatch) => {
        if (scope === ontologicalModelActionTypes.SAVE_ONTOLOGICAL_MODEL ||
            scope === connectionsActionTypes.RELOAD_ONTOLOGY)
            dispatch(actionCreators.setObjectsOutdated());
    },
    [ontologicalModelActionTypes.SAVE_ONTOLOGICAL_MODEL_COMPLETED]: loadDocumentObjects,
    [ontologicalModelActionTypes.SAVE_ONTOLOGICAL_MODEL_REJECTED]: loadDocumentObjects,
    [connectionsActionTypes.RELOAD_ONTOLOGY_COMPLETED]: loadDocumentObjects,
    [connectionsActionTypes.RELOAD_ONTOLOGY_REJECTED]: loadDocumentObjects
});
